/* eslint-disable sonarjs/max-switch-cases */
import { ChangeConfigurationStatus, ChargerCommandType, TimelineType } from '@core/api/charger-details/charger-details.enums';
import {
  AdaptivePowerCapChangeEvent,
  AuthorizationFailedEvent,
  AuthorizationSuccessfulEvent,
  BootNotificationEvent,
  CSFaultedEvent,
  CSStateChangeEvent,
  ChargerActivityPowerGraphData,
  ChargerActivityPowerGraphUnitData,
  ChargerActivityResponse,
  ChargerActivityWebsocketMessage,
  ChargerCommandRequest,
  ChargerForceDisconnectCommandRequest,
  ChargerHardResetCommandRequest,
  ChargerSoftResetCommandRequest,
  ChargingFaultedEvent,
  ChargingTimelineEvent,
  ChargingTimelineResponse,
  ChargingTimelineWebsocketMessage,
  ConfigurationKeyValue,
  CustomCommandEvent,
  EvseActivityGraphData,
  EvseActivityGraphDataUnit,
  EvseActivityResponse,
  EvseActivityWebsocketMessage,
  EvseFaultedEvent,
  EvsePowerUnitData,
  EvseReference,
  ForceDisconnectEvent,
  HardResetEvent,
  ManualPriorityClearedEvent,
  ManualPrioritySetEvent,
  PowerLimitChangeEvent,
  PowerLimitChangeRejectedEvent,
  PowerOverrideClearedEvent,
  SessionClosedEvent,
  SessionOpenedEvent,
  SoftResetEvent,
  StatusNotificationEvent,
  TimelineBaseEvent,
  TransactionBlockedEvent,
  TransactionRemoteStartEvent,
  TransactionRemoteStopEvent,
  TransactionStartedEvent,
  TransactionStoppedEvent,
  TriggerMessageEvent,
  UnknownVehiclePluggedInEvent,
  UnlockConnectorEvent,
  UnrecognizableOCPPMessageEvent,
  VehiclePluggedInEvent,
  VehicleUnpluggedEvent,
} from '@core/api/charger-details/charger-details.models';

import { mapAlertDtoApiModelToStoreModel } from '@domain/alerting/alerting-models.mappings';
import {
  mapConnectorTypeApiModelEnumToStoreModelEnum,
  mapEvseStatusApiModelEnumToStoreModelEnum,
} from '@domain/single-site/single-site-enums.mappings';
import {
  mapChargerOperationalStatusApiModelEnumToStoreModelEnum,
  mapChargerStatusApiModelEnumToStoreModelEnum,
} from '@domain/sites/sites-enums.mappings';

import { isDefined } from '@shared/utils/is-defined';
import { wToKw } from '@shared/utils/w-to-kw';
import { whToKwh } from '@shared/utils/wh-to-kwh';

import {
  mapChangeConfigurationStatusApiModelEnumToStoreModelEnum,
  mapTimelineEventSeverityApiModelEnumToStoreModelEnum,
  mapTransactionStopSourceApiModelEnumToStoreModelEnum,
} from './charger-details-enums.mappings';
import { ChargerCommandTypeStoreModelEnum, TimelineTypeStoreModelEnum } from './charger-details.enums';
import {
  AdaptivePowerCapChangeEventStoreModel,
  AuthorizationFailedEventStoreModel,
  AuthorizationSuccessfulEventStoreModel,
  BootNotificationEventStoreModel,
  CSFaultedEventStoreModel,
  CSStateChangeEventStoreModel,
  ChargerActivityPowerGraphDataStoreModel,
  ChargerActivityPowerGraphUnitDataStoreModel,
  ChargerActivityResponseStoreModel,
  ChargerActivityWebsocketMessageStoreModel,
  ChargerCommandRequestStoreModel,
  ChargerForceDisconnectCommandRequestStoreModel,
  ChargerHardResetCommandRequestStoreModel,
  ChargerSoftResetCommandRequestStoreModel,
  ChargingFaultedEventStoreModel,
  ChargingTimelineEventStoreModel,
  ChargingTimelineResponseStoreModel,
  ChargingTimelineWebsocketMessageStoreModel,
  ConfigurationChangeResultStoreModel,
  ConfigurationKeyChangeStoreModel,
  ConfigurationKeyValueStoreModel,
  CustomCommandEventStoreModel,
  EvseActivityGraphDataStoreModel,
  EvseActivityGraphDataUnitStoreModel,
  EvseActivityResponseStoreModel,
  EvseActivityWebsocketMessageStoreModel,
  EvseFaultedEventStoreModel,
  EvsePowerUnitDataStoreModel,
  EvseReferenceStoreModel,
  ForceDisconnectEventStoreModel,
  HardResetEventStoreModel,
  ManualPriorityClearedEventStoreModel,
  ManualPrioritySetEventStoreModel,
  PowerLimitChangeEventStoreModel,
  PowerLimitChangeRejectedEventStoreModel,
  PowerOverrideClearedEventStoreModel,
  SessionClosedEventStoreModel,
  SessionOpenedEventStoreModel,
  SoftResetEventStoreModel,
  StatusNotificationEventStoreModel,
  TimelineBaseEventStoreModel,
  TransactionBlockedEventStoreModel,
  TransactionRemoteStartEventStoreModel,
  TransactionRemoteStopEventStoreModel,
  TransactionStartedEventStoreModel,
  TransactionStoppedEventStoreModel,
  TriggerMessageEventStoreModel,
  UnknownVehiclePluggedInEventStoreModel,
  UnlockConnectorEventStoreModel,
  UnrecognizableOCPPMessageEventStoreModel,
  VehiclePluggedInEventStoreModel,
  VehicleUnpluggedEventStoreModel,
} from './charger-details.models';

export const mapChargerActivityResponseApiModelToStoreModel = (response: ChargerActivityResponse): ChargerActivityResponseStoreModel => {
  return {
    chargerName: response.chargerName,
    powerGraphData: mapChargerActivityPowerGraphDataApiModelToStoreModel(response.powerGraphData),
    evses: response.evses.map((evse) => mapEvseReferenceApiModelToStoreModel(evse)),
    make: response.make,
    model: response.model,
    maxPowerKw: makeItPositive(wToKw(response.maxPower)),
    ocppVersion: response.ocppVersion,
    serialNumber: response.serialNumber,
    firmwareVersion: response.firmwareVersion,
    chargerIdentity: response.chargerIdentity,
    picture: response.picture,
    alerts: response.alerts.map((alert) => mapAlertDtoApiModelToStoreModel(alert)),
    activePowerKw: makeItPositive(wToKw(response.activePower)),
    importLimitKw: makeItPositive(wToKw(response.importLimit)),
    csmsProxyLatencyMs: response.csmsProxyLatencyMs,
    chargerOperationalState: mapChargerOperationalStatusApiModelEnumToStoreModelEnum(response.chargerOperationalState),
    chargerStatus: mapChargerStatusApiModelEnumToStoreModelEnum(response.chargerStatus),
    chargerHasActiveSession: response.chargerHasActiveSession,
    powerSplitSiblingChargers: response.powerSplitSiblingChargers,
  };
};

export const mapEvseReferenceApiModelToStoreModel = (reference: EvseReference): EvseReferenceStoreModel => {
  return {
    id: reference.id,
    name: reference.name,
    connectorType: mapConnectorTypeApiModelEnumToStoreModelEnum(reference.connectorType),
  };
};

export const mapChargerActivityWebsocketMessageApiModelToStoreModel = (
  message: ChargerActivityWebsocketMessage
): ChargerActivityWebsocketMessageStoreModel => {
  return {
    powerGraphData: mapChargerActivityPowerGraphDataApiModelToStoreModel(message.powerGraphData),
    alerts: message.alerts.map((alert) => mapAlertDtoApiModelToStoreModel(alert)),
    activePowerKw: makeItPositive(wToKw(message.activePower)),
    csmsProxyLatencyMs: message.csmsProxyLatencyMs,
    chargerOperationalState: mapChargerOperationalStatusApiModelEnumToStoreModelEnum(message.chargerOperationalState),
    chargerStatus: mapChargerStatusApiModelEnumToStoreModelEnum(message.chargerStatus),
    chargerHasActiveSession: message.chargerHasActiveSession,
  };
};

export const mapChargerActivityPowerGraphDataApiModelToStoreModel = (
  powerGraphData: ChargerActivityPowerGraphData
): ChargerActivityPowerGraphDataStoreModel => {
  return {
    data: powerGraphData.data?.map((data) => mapChargerActivityPowerGraphUnitDataApiModelToStoreModel(data)),
  };
};

export const mapChargerActivityPowerGraphUnitDataApiModelToStoreModel = (
  unit: ChargerActivityPowerGraphUnitData
): ChargerActivityPowerGraphUnitDataStoreModel => {
  return {
    timestamp: unit.timestamp,
    powerUnits: unit.powerUnits.map((powerUnit) => mapEvsePowerUnitDataApiModelToStoreModel(powerUnit)),
    charging: unit.charging,
    faulted: unit.faulted,
  };
};

export const mapEvsePowerUnitDataApiModelToStoreModel = (unit: EvsePowerUnitData): EvsePowerUnitDataStoreModel => {
  return {
    id: unit.id,
    powerKw: unit.power && wToKw(unit.power),
  };
};

const makeItPositive = <T extends number | null | undefined>(value: T): T => {
  if (isDefined(value)) {
    const valueAsNumber = value as number;

    if (valueAsNumber < 0) {
      console.error('Negative value encountered where positive was expected:', value);
    }

    return Math.abs(valueAsNumber) as T;
  }
  return value;
};

export const mapEvseActivityResponseApiModelToStoreModel = (response: EvseActivityResponse): EvseActivityResponseStoreModel => {
  return {
    evseStatus: mapEvseStatusApiModelEnumToStoreModelEnum(response.evseStatus),
    vehicleUUID: response.vehicleUUID,
    vehicleName: response.vehicleName,
    soc: response.soc,
    departureTime: response.departureTime,
    forecastedDepartureSoc: response.forecastedDepartureSoc,
    activePowerKw: response.activePower && makeItPositive(wToKw(response.activePower)),
    maxPowerKw: makeItPositive(wToKw(response.maxPower)),
    evsePowerLimitKw: response.evsePowerLimit && makeItPositive(wToKw(response.evsePowerLimit)),
    powerOverride: response.powerOverride,
    hasActiveSession: response.hasActiveSession,
    deliveredEnergyKwh: response.deliveredEnergy && makeItPositive(whToKwh(response.deliveredEnergy)),
    remainingEnergyKwh: response.remainingEnergy && makeItPositive(whToKwh(response.remainingEnergy)),
    graphData: mapEvseActivityGraphDataApiModelToStoreModel(response.graphData),
  };
};

export const mapEvseActivityWebsocketMessageApiModelToStoreModel = (
  message: EvseActivityWebsocketMessage
): EvseActivityWebsocketMessageStoreModel => {
  return {
    evseStatus: mapEvseStatusApiModelEnumToStoreModelEnum(message.evseStatus),
    vehicleUUID: message.vehicleUUID,
    vehicleName: message.vehicleName,
    soc: message.soc,
    departureTime: message.departureTime,
    forecastedDepartureSoc: message.forecastedDepartureSoc,
    activePowerKw: message.activePower && makeItPositive(wToKw(message.activePower)),
    evsePowerLimitKw: message.evsePowerLimit && makeItPositive(wToKw(message.evsePowerLimit)),
    powerOverride: message.powerOverride,
    hasActiveSession: message.hasActiveSession,
    deliveredEnergyKwh: message.deliveredEnergy && makeItPositive(whToKwh(message.deliveredEnergy)),
    remainingEnergyKwh: message.remainingEnergy && makeItPositive(whToKwh(message.remainingEnergy)),
    graphData: mapEvseActivityGraphDataApiModelToStoreModel(message.graphData),
  };
};

export const mapEvseActivityGraphDataApiModelToStoreModel = (graphData: EvseActivityGraphData): EvseActivityGraphDataStoreModel => {
  return {
    data: graphData.data?.map((data) => mapEvseActivityGraphDataUnitApiModelToStoreModel(data)),
  };
};

export const mapEvseActivityGraphDataUnitApiModelToStoreModel = (data: EvseActivityGraphDataUnit): EvseActivityGraphDataUnitStoreModel => {
  return {
    timestamp: data.timestamp,
    soc: data.soc ?? undefined,
    powerKw: (data.power && makeItPositive(wToKw(data.power))) ?? undefined,
    powerLimitKw: (data.powerLimit && makeItPositive(wToKw(data.powerLimit))) ?? undefined,
    faulted: data.faulted,
  };
};

export const mapChargingTimelineResponseApiModelToStoreModel = (response: ChargingTimelineResponse): ChargingTimelineResponseStoreModel => {
  return {
    events: response.events.map((event) => mapChargingTimelineEventApiModelToStoreModel(event)),
    limit: response.limit,
    before: response.before,
    oldest: response.oldest,
  };
};

export const mapChargingTimelineWebsocketMessageApiModelToStoreModel = (
  message: ChargingTimelineWebsocketMessage
): ChargingTimelineWebsocketMessageStoreModel => {
  return {
    event: mapChargingTimelineEventApiModelToStoreModel(message.event),
  };
};

export const mapChargingTimelineEventApiModelToStoreModel = (event: ChargingTimelineEvent): ChargingTimelineEventStoreModel => {
  switch (event.type) {
    case TimelineType.AdaptivePowerCapChangeEvent: {
      return mapAdaptivePowerCapChangeEventApiModelToStoreModel(event);
    }
    case TimelineType.AuthorizationFailedEvent: {
      return mapAuthorizationFailedEventApiModelToStoreModel(event);
    }
    case TimelineType.AuthorizationSuccessfulEvent: {
      return mapRfidAuthorizationSuccessfulEventApiModelToStoreModel(event);
    }
    case TimelineType.BootNotificationEvent: {
      return mapBootNotificationEventApiModelToStoreModel(event);
    }
    case TimelineType.ChargingFaultedEvent: {
      return mapChargingFaultedEventApiModelToStoreModel(event);
    }
    case TimelineType.CsFaultedEvent: {
      return mapCSFaultedEventApiModelToStoreModel(event);
    }
    case TimelineType.EvseFaultedEvent: {
      return mapEvseFaultedEventApiModelToStoreModel(event);
    }
    case TimelineType.StatusNotificationEvent: {
      return mapStatusNotificationEventApiModelToStoreModel(event);
    }
    case TimelineType.CsStateChangeEvent: {
      return mapCSStateChangeEventApiModelToStoreModel(event);
    }
    case TimelineType.CustomCommandEvent: {
      return mapCustomCommandEventApiModelToStoreModel(event);
    }
    case TimelineType.ForceDisconnectEvent: {
      return mapForceDisconnectEventApiModelToStoreModel(event);
    }
    case TimelineType.HardResetEvent: {
      return mapHardResetEventApiModelToStoreModel(event);
    }
    case TimelineType.SoftResetEvent: {
      return mapSoftResetEventApiModelToStoreModel(event);
    }
    case TimelineType.PowerLimitChangeEvent: {
      return mapPowerLimitChangeEventApiModelToStoreModel(event);
    }
    case TimelineType.PowerLimitChangeRejectedEvent: {
      return mapPowerLimitChangeRejectedEventApiModelToStoreModel(event);
    }
    case TimelineType.PowerOverrideClearedEvent: {
      return mapPowerOverrideClearedEventApiModelToStoreModel(event);
    }
    case TimelineType.ManualPrioritySetEvent: {
      return mapManualPrioritySetEventApiModelToStoreModel(event);
    }
    case TimelineType.ManualPriorityClearedEvent: {
      return mapManualPriorityClearedEventApiModelToStoreModel(event);
    }
    case TimelineType.SessionClosedEvent: {
      return mapSessionClosedEventApiModelToStoreModel(event);
    }
    case TimelineType.SessionOpenedEvent: {
      return mapSessionOpenedEventApiModelToStoreModel(event);
    }
    case TimelineType.TransactionBlockedEvent: {
      return mapTransactionBlockedEventApiModelToStoreModel(event);
    }
    case TimelineType.TransactionStartedEvent: {
      return mapTransactionStartedEventApiModelToStoreModel(event);
    }
    case TimelineType.TransactionRemoteStartEvent: {
      return mapTransactionRemoteStartEventApiModelToStoreModel(event);
    }
    case TimelineType.TransactionStoppedEvent: {
      return mapTransactionStoppedEventApiModelToStoreModel(event);
    }
    case TimelineType.TransactionRemoteStopEvent: {
      return mapTransactionRemoteStopEventApiModelToStoreModel(event);
    }
    case TimelineType.TriggerMessageEvent: {
      return mapTriggerMessageEventApiModelToStoreModel(event);
    }
    case TimelineType.UnlockConnectorEvent: {
      return mapUnlockConnectorEventApiModelToStoreModel(event);
    }
    case TimelineType.UnknownVehiclePluggedInEvent: {
      return mapUnknownVehiclePluggedInEventApiModelToStoreModel(event);
    }
    case TimelineType.UnrecognizableOcppMessageEvent: {
      return mapUnrecognizableOCPPMessageEventApiModelToStoreModel(event);
    }
    case TimelineType.VehiclePluggedInEvent: {
      return mapVehiclePluggedInEventApiModelToStoreModel(event);
    }
    case TimelineType.VehicleUnpluggedEvent: {
      return mapVehicleUnpluggedEventApiModelToStoreModel(event);
    }
    default: {
      return event;
    }
  }
};

export const mapAdaptivePowerCapChangeEventApiModelToStoreModel = (
  event: AdaptivePowerCapChangeEvent
): AdaptivePowerCapChangeEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.AdaptivePowerCapChangeEvent,
    transactionId: event.transactionId,
    measuredPowerKw: event.measuredPower == null ? undefined : wToKw(event.measuredPower),
    appliedPowerLimitKw: event.appliedPowerLimit == null ? undefined : wToKw(event.appliedPowerLimit),
    previousAdaptivePowerCapKw: event.previousAdaptivePowerCap == null ? undefined : wToKw(event.previousAdaptivePowerCap),
    newAdaptivePowerCapKw: event.newAdaptivePowerCap == null ? undefined : wToKw(event.newAdaptivePowerCap),
    capDeltaKw: event.capDelta == null ? undefined : wToKw(event.capDelta),
  };
};

export const mapAuthorizationFailedEventApiModelToStoreModel = (event: AuthorizationFailedEvent): AuthorizationFailedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.AuthorizationFailedEvent,
    idTag: event.idTag,
  };
};

export const mapRfidAuthorizationSuccessfulEventApiModelToStoreModel = (
  event: AuthorizationSuccessfulEvent
): AuthorizationSuccessfulEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.AuthorizationSuccessfulEvent,
    idTagAssignee: event.idTagAssignee,
  };
};

export const mapBootNotificationEventApiModelToStoreModel = (event: BootNotificationEvent): BootNotificationEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.BootNotificationEvent,
    ocppMessage: event.ocppMessage,
  };
};

export const mapChargingFaultedEventApiModelToStoreModel = (event: ChargingFaultedEvent): ChargingFaultedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.ChargingFaultedEvent,
    transactionId: event.transactionId,
    evAssetId: event.evAssetId,
    idTagAssignee: event.idTagAssignee,
    ocppMessage: event.ocppMessage,
  };
};

export const mapCSFaultedEventApiModelToStoreModel = (event: CSFaultedEvent): CSFaultedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.CsFaultedEvent,
    ocppMessage: event.ocppMessage,
  };
};

export const mapEvseFaultedEventApiModelToStoreModel = (event: EvseFaultedEvent): EvseFaultedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.EvseFaultedEvent,
    ocppMessage: event.ocppMessage,
  };
};

export const mapStatusNotificationEventApiModelToStoreModel = (event: StatusNotificationEvent): StatusNotificationEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.StatusNotificationEvent,
    status: event.status,
    errorCode: event.errorCode,
    info: event.info,
    vendorErrorCode: event.vendorErrorCode,
  };
};

export const mapCSStateChangeEventApiModelToStoreModel = (event: CSStateChangeEvent): CSStateChangeEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.CsStateChangeEvent,
    operationalState: mapChargerOperationalStatusApiModelEnumToStoreModelEnum(event.operationalState),
  };
};

export const mapCustomCommandEventApiModelToStoreModel = (event: CustomCommandEvent): CustomCommandEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.CustomCommandEvent,
  };
};

export const mapForceDisconnectEventApiModelToStoreModel = (event: ForceDisconnectEvent): ForceDisconnectEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.ForceDisconnectEvent,
  };
};

export const mapHardResetEventApiModelToStoreModel = (event: HardResetEvent): HardResetEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.HardResetEvent,
  };
};

export const mapSoftResetEventApiModelToStoreModel = (event: SoftResetEvent): SoftResetEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.SoftResetEvent,
  };
};

export const mapPowerLimitChangeEventApiModelToStoreModel = (event: PowerLimitChangeEvent): PowerLimitChangeEventStoreModel => {
  const chargingPowerLimitKw = event.chargingPowerLimit && wToKw(event.chargingPowerLimit);

  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.PowerLimitChangeEvent,
    evAssetId: event.evAssetId,
    idTagAssignee: event.idTagAssignee,
    transactionId: event.transactionId,
    chargingPowerLimitKw: chargingPowerLimitKw,
  };
};

export const mapPowerLimitChangeRejectedEventApiModelToStoreModel = (
  event: PowerLimitChangeRejectedEvent
): PowerLimitChangeRejectedEventStoreModel => {
  const chargingPowerLimitKw = event.chargingPowerLimit && wToKw(event.chargingPowerLimit);

  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.PowerLimitChangeRejectedEvent,
    evAssetId: event.evAssetId,
    idTagAssignee: event.idTagAssignee,
    transactionId: event.transactionId,
    chargingPowerLimitKw: chargingPowerLimitKw,
  };
};

export const mapPowerOverrideClearedEventApiModelToStoreModel = (event: PowerOverrideClearedEvent): PowerOverrideClearedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.PowerOverrideClearedEvent,
  };
};

export const mapManualPrioritySetEventApiModelToStoreModel = (event: ManualPrioritySetEvent): ManualPrioritySetEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.ManualPrioritySetEvent,
  };
};

export const mapManualPriorityClearedEventApiModelToStoreModel = (
  event: ManualPriorityClearedEvent
): ManualPriorityClearedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.ManualPriorityClearedEvent,
  };
};

export const mapSessionClosedEventApiModelToStoreModel = (event: SessionClosedEvent): SessionClosedEventStoreModel => {
  return { ...mapTimelineBaseEventApiModelToStoreModel(event), type: TimelineTypeStoreModelEnum.SessionClosedEvent, reason: event.reason };
};

export const mapSessionOpenedEventApiModelToStoreModel = (event: SessionOpenedEvent): SessionOpenedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.SessionOpenedEvent,
  };
};

export const mapTransactionBlockedEventApiModelToStoreModel = (event: TransactionBlockedEvent): TransactionBlockedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TransactionBlockedEvent,
    idTag: event.idTag,
  };
};

export const mapTransactionStartedEventApiModelToStoreModel = (event: TransactionStartedEvent): TransactionStartedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TransactionStartedEvent,
    transactionId: event.transactionId,
    pickedUpOffline: event.pickedUpOffline,
    evAssetId: event.evAssetId,
    idTagAssignee: event.idTagAssignee,
  };
};

export const mapTransactionRemoteStartEventApiModelToStoreModel = (
  event: TransactionRemoteStartEvent
): TransactionRemoteStartEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TransactionRemoteStartEvent,
    idTagAssignee: event.idTagAssignee,
  };
};

export const mapTransactionStoppedEventApiModelToStoreModel = (event: TransactionStoppedEvent): TransactionStoppedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TransactionStoppedEvent,
    transactionId: event.transactionId,
    evAssetId: event.evAssetId,
    idTagAssignee: event.idTagAssignee,
    source: mapTransactionStopSourceApiModelEnumToStoreModelEnum(event.source),
    ocppId: event.ocppId,
    reason: event.reason,
    soc: event.soc,
  };
};

export const mapTransactionRemoteStopEventApiModelToStoreModel = (
  event: TransactionRemoteStopEvent
): TransactionRemoteStopEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TransactionRemoteStopEvent,
  };
};

export const mapTriggerMessageEventApiModelToStoreModel = (event: TriggerMessageEvent): TriggerMessageEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.TriggerMessageEvent,
    requestedMessage: event.requestedMessage,
  };
};

export const mapUnlockConnectorEventApiModelToStoreModel = (event: UnlockConnectorEvent): UnlockConnectorEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.UnlockConnectorEvent,
  };
};

export const mapUnknownVehiclePluggedInEventApiModelToStoreModel = (
  event: UnknownVehiclePluggedInEvent
): UnknownVehiclePluggedInEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.UnknownVehiclePluggedInEvent,
    idTagAssignee: event.idTagAssignee,
  };
};

export const mapUnrecognizableOCPPMessageEventApiModelToStoreModel = (
  event: UnrecognizableOCPPMessageEvent
): UnrecognizableOCPPMessageEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.UnrecognizableOcppMessageEvent,
    ocppMessage: event.ocppMessage,
  };
};

export const mapVehiclePluggedInEventApiModelToStoreModel = (event: VehiclePluggedInEvent): VehiclePluggedInEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.VehiclePluggedInEvent,
    idTagAssignee: event.idTagAssignee,
    evAssetId: event.evAssetId,
  };
};

export const mapVehicleUnpluggedEventApiModelToStoreModel = (event: VehicleUnpluggedEvent): VehicleUnpluggedEventStoreModel => {
  return {
    ...mapTimelineBaseEventApiModelToStoreModel(event),
    type: TimelineTypeStoreModelEnum.VehicleUnpluggedEvent,
    idTagAssignee: event.idTagAssignee,
    evAssetId: event.evAssetId,
  };
};

export const mapTimelineBaseEventApiModelToStoreModel = (event: TimelineBaseEvent): TimelineBaseEventStoreModel => {
  return {
    timestamp: event.timestamp,
    assetId: event.assetId,
    actor: event.actor,
    severity: mapTimelineEventSeverityApiModelEnumToStoreModelEnum(event.severity),
  };
};

export const mapChargerCommandRequestStoreModelToApiModel = (request: ChargerCommandRequestStoreModel): ChargerCommandRequest => {
  switch (request.type) {
    case ChargerCommandTypeStoreModelEnum.HardReset: {
      return mapChargerHardResetCommandRequestStoreModelToApiModel(request);
    }
    case ChargerCommandTypeStoreModelEnum.SoftReset: {
      return mapChargerSoftResetCommandRequestStoreModelToApiModel(request);
    }
    case ChargerCommandTypeStoreModelEnum.ForceDisconnect: {
      return mapChargerForceDisconnectCommandRequestStoreModelToApiModel(request);
    }
  }
};

export const mapChargerHardResetCommandRequestStoreModelToApiModel = (
  _request: ChargerHardResetCommandRequestStoreModel
): ChargerHardResetCommandRequest => {
  return {
    type: ChargerCommandType.HardReset,
  };
};

export const mapChargerSoftResetCommandRequestStoreModelToApiModel = (
  _request: ChargerSoftResetCommandRequestStoreModel
): ChargerSoftResetCommandRequest => {
  return {
    type: ChargerCommandType.SoftReset,
  };
};

export const mapChargerForceDisconnectCommandRequestStoreModelToApiModel = (
  _request: ChargerForceDisconnectCommandRequestStoreModel
): ChargerForceDisconnectCommandRequest => {
  return {
    type: ChargerCommandType.ForceDisconnect,
  };
};

export const mapConfigurationKeyValueApiModelToStoreModel = (
  configurationKeyValue: ConfigurationKeyValue
): ConfigurationKeyValueStoreModel => {
  return {
    key: configurationKeyValue.key,
    value: configurationKeyValue.value ?? '',
    readonly: configurationKeyValue.readonly,
  };
};

export const mapChangeConfigurationStatusesApiModelToStoreModel = (
  changeConfigurationStatuses: Record<string, ChangeConfigurationStatus>
): ConfigurationChangeResultStoreModel[] => {
  return Object.entries(changeConfigurationStatuses).map(([key, status]) => {
    return {
      key,
      status: mapChangeConfigurationStatusApiModelEnumToStoreModelEnum(status),
    };
  });
};

export const mapConfigurationKeyChangesStoreModelToKeyValues = (changes: ConfigurationKeyChangeStoreModel[]): Record<string, string> => {
  return Object.fromEntries(changes.map((change) => [change.key, change.value]));
};
